import type {
  AggregatedProjectData,
  AICheckResult,
  AICheckRunResult,
  CheckCategory,
  CheckImportance,
  CheckStatus,
  ConsistencyCheckResult,
  FreeformCheckResult,
  FreeformFinding,
  RuleSeverity,
} from "@/types/aicheck";
import { collectEvidenceEntries } from "./evidence-traceability";
import {
  type ProjectDataReferenceTarget,
  parseProjectDataReference,
} from "./project-data-reference";

type RecordLike = Record<string, unknown>;

export type ProjectDataOverviewIssueSeverity = "critical" | "warning" | "info";

export type ProjectDataOverviewIssueSource = "ai" | "rule" | "freeform";

export interface ProjectDataOverviewIssueReference {
  kind: "project" | "day" | "item";
  source: string | null;
  dayIndex?: number;
  dayNumber?: number;
  itemIndex?: number;
  itemId?: string;
  itemType?: string;
  itemKey?: string;
  evidenceIndex?: number;
}

export interface ProjectDataOverviewIssue {
  id: string;
  checkId: string;
  checkName: string;
  category: CheckCategory;
  sourceKind: ProjectDataOverviewIssueSource;
  status: CheckStatus | RuleSeverity | "pass";
  importance: CheckImportance | "medium";
  severity: ProjectDataOverviewIssueSeverity;
  title: string;
  summary: string;
  detail: string;
  reference: ProjectDataOverviewIssueReference;
}

export interface ProjectDataOverviewIssueIndex {
  allIssues: ProjectDataOverviewIssue[];
  projectIssues: ProjectDataOverviewIssue[];
  issuesByDayIndex: Map<number, ProjectDataOverviewIssue[]>;
  issuesByItemKey: Map<string, ProjectDataOverviewIssue[]>;
  totals: Record<ProjectDataOverviewIssueSeverity, number>;
}

interface DataIndex {
  dayNumberToIndex: Map<number, number>;
  itemIdToLocation: Map<string, { dayIndex: number; itemIndex: number }>;
  itemIdByLocation: Map<string, string>;
  itemTypeByLocation: Map<string, string | null>;
}

interface IssueDraft {
  id: string;
  checkId: string;
  checkName: string;
  category: CheckCategory;
  sourceKind: ProjectDataOverviewIssueSource;
  status: CheckStatus | RuleSeverity | "pass";
  importance: CheckImportance | "medium";
  severity: ProjectDataOverviewIssueSeverity;
  title: string;
  summary: string;
  detail: string;
  references: ProjectDataOverviewIssueReference[];
}

export function buildProjectDataOverviewIssueIndex(
  projectData: AggregatedProjectData | null | undefined,
  result: AICheckRunResult | null | undefined,
  freeformResult?: FreeformCheckResult | null | undefined,
): ProjectDataOverviewIssueIndex {
  const empty = createEmptyIssueIndex();
  if (!projectData || (!result && !freeformResult)) return empty;

  const dataIndex = buildDataIndex(projectData);
  const issueMap = new Map<string, ProjectDataOverviewIssue>();

  if (result) {
    for (const aiResult of result.aiResults) {
      if (!isAIIssueWorthy(aiResult)) continue;
      addIssueDrafts(issueMap, buildAIIssueDrafts(aiResult, dataIndex));
    }

    for (const consistencyResult of result.consistencyResults) {
      if (consistencyResult.passed && !consistencyResult.evaluationError) {
        continue;
      }
      addIssueDrafts(
        issueMap,
        buildConsistencyIssueDrafts(consistencyResult, dataIndex),
      );
    }
  }

  if (freeformResult) {
    addIssueDrafts(
      issueMap,
      buildFreeformIssueDrafts(freeformResult, dataIndex),
    );
  }

  const allIssues = Array.from(issueMap.values()).sort(compareIssues);
  const index = createEmptyIssueIndex();
  index.allIssues.push(...allIssues);

  for (const issue of allIssues) {
    index.totals[issue.severity] += 1;
    const dayIndex = issue.reference.dayIndex;
    if (typeof dayIndex === "number") {
      appendMapValue(index.issuesByDayIndex, dayIndex, issue);
    } else {
      index.projectIssues.push(issue);
    }
    if (issue.reference.itemKey) {
      appendMapValue(index.issuesByItemKey, issue.reference.itemKey, issue);
    }
  }

  return index;
}

function createEmptyIssueIndex(): ProjectDataOverviewIssueIndex {
  return {
    allIssues: [],
    projectIssues: [],
    issuesByDayIndex: new Map(),
    issuesByItemKey: new Map(),
    totals: { critical: 0, warning: 0, info: 0 },
  };
}

function addIssueDrafts(
  issueMap: Map<string, ProjectDataOverviewIssue>,
  drafts: IssueDraft[],
) {
  for (const draft of drafts) {
    const references: ProjectDataOverviewIssueReference[] =
      draft.references.length > 0
        ? preferSpecificReferences(draft.references)
        : [{ kind: "project", source: null }];
    for (const reference of references) {
      const issue: ProjectDataOverviewIssue = {
        id: `${draft.id}:${referenceKey(reference)}`,
        checkId: draft.checkId,
        checkName: draft.checkName,
        category: draft.category,
        sourceKind: draft.sourceKind,
        status: draft.status,
        importance: draft.importance,
        severity: draft.severity,
        title: draft.title,
        summary: draft.summary,
        detail: draft.detail,
        reference,
      };
      if (!issueMap.has(issue.id)) {
        issueMap.set(issue.id, issue);
      }
    }
  }
}

function buildAIIssueDrafts(
  result: AICheckResult,
  dataIndex: DataIndex,
): IssueDraft[] {
  const findings = asRecords(result.result.findings);
  if (findings.length > 0) {
    const drafts = findings
      .map((finding, index) =>
        buildAIFindingDraft(result, finding, index, dataIndex),
      )
      .filter((draft): draft is IssueDraft => draft != null);
    if (drafts.length > 0) return drafts;
  }

  return [
    {
      id: `${result.checkId}:result`,
      checkId: result.checkId,
      checkName: result.checkName,
      category: result.category,
      sourceKind: "ai",
      status: result.status,
      importance: result.importance ?? "medium",
      severity: severityFromAIResult(result),
      title: result.checkName,
      summary: result.summary ?? result.error ?? "No issue summary available.",
      detail: result.summary ?? result.error ?? "No issue detail available.",
      references: referencesFromEvidence(result.result, dataIndex),
    },
  ];
}

function buildAIFindingDraft(
  result: AICheckResult,
  finding: RecordLike,
  index: number,
  dataIndex: DataIndex,
): IssueDraft | null {
  const severity = severityFromFinding(finding, result);
  if (severity === "info" && result.status === "pass") return null;

  const title =
    firstText(finding.check_point, finding.finding, finding.topic) ??
    result.checkName;
  const summary =
    firstText(
      finding.finding,
      finding.summary,
      finding.issue,
      result.summary,
    ) ?? "No issue summary available.";
  const detail = [
    labeledLine("Finding", finding.finding),
    labeledLine("Evidence", finding.evidence),
    labeledLine("Recommendation", finding.recommendation),
    labeledLine("Summary", result.summary),
  ]
    .filter((value): value is string => value != null)
    .join("\n");
  const references = referencesFromFinding(finding, result.result, dataIndex);

  return {
    id: `${result.checkId}:finding:${index}`,
    checkId: result.checkId,
    checkName: result.checkName,
    category: result.category,
    sourceKind: "ai",
    status: result.status,
    importance: result.importance ?? "medium",
    severity,
    title,
    summary,
    detail: detail || summary,
    references,
  };
}

function buildConsistencyIssueDrafts(
  result: ConsistencyCheckResult,
  dataIndex: DataIndex,
): IssueDraft[] {
  const summary = result.evaluationError
    ? `Evaluation error: ${result.message}`
    : result.message;
  return [
    {
      id: `${result.ruleId}:result`,
      checkId: result.ruleId,
      checkName: result.ruleName,
      category: result.category,
      sourceKind: "rule",
      status: result.evaluationError ? "error" : result.severity,
      importance: result.importance ?? "medium",
      severity: severityFromConsistencyResult(result),
      title: result.ruleName,
      summary,
      detail: [
        summary,
        labeledLine("Source value", result.sourceValue),
        labeledLine("Target value", result.targetValue),
      ]
        .filter((value): value is string => value != null)
        .join("\n"),
      references: referencesFromTexts(
        [result.message, result.sourceValue, result.targetValue],
        dataIndex,
      ),
    },
  ];
}

function buildFreeformIssueDrafts(
  result: FreeformCheckResult,
  dataIndex: DataIndex,
): IssueDraft[] {
  return result.findings.map((finding, index) => {
    const title = firstText(finding.finding) ?? result.stage;
    const evidenceDetail = finding.evidence
      .map((evidence, evidenceIndex) =>
        [
          `Evidence ${evidenceIndex + 1}:`,
          labeledLine("Source", evidence.source),
          labeledLine("JSON path", evidence.json_path),
          labeledLine("Quote", evidence.quote),
        ]
          .filter((value): value is string => value != null)
          .join("\n"),
      )
      .filter(Boolean)
      .join("\n\n");
    const detail = [
      labeledLine("Finding type", finding.finding_type),
      labeledLine("Confidence", finding.confidence),
      labeledLine("Finding", finding.finding),
      evidenceDetail || null,
      labeledLine("Recommendation", finding.recommendation),
    ]
      .filter((value): value is string => value != null)
      .join("\n\n");

    return {
      id: `${result.templateId}:finding:${index}`,
      checkId: result.templateId,
      checkName: `${result.stage} Freeform Review`,
      category: "need_review",
      sourceKind: "freeform",
      status: statusFromFreeformFinding(finding),
      importance: importanceFromFreeformFinding(finding),
      severity: finding.severity,
      title,
      summary: finding.finding,
      detail: detail || finding.finding,
      references: referencesFromFreeformFinding(finding, dataIndex),
    };
  });
}

function referencesFromFreeformFinding(
  finding: FreeformFinding,
  dataIndex: DataIndex,
): ProjectDataOverviewIssueReference[] {
  const references: ProjectDataOverviewIssueReference[] = [];
  const itemType = normalizeItemType(finding.item_reference ?? null);

  if (finding.day_number != null) {
    references.push(
      normalizeReference(
        {
          kind: itemType ? "itineraryItem" : "itineraryDay",
          dayNumber: finding.day_number,
          itemType,
          source: `day_number:${finding.day_number}`,
        },
        dataIndex,
      ),
    );
  }

  if (finding.json_path) {
    const reference = referenceFromSource(finding.json_path, dataIndex);
    if (reference) references.push(reference);
  }

  if (finding.item_reference) {
    references.push(
      ...referencesFromTexts([finding.item_reference], dataIndex),
    );
  }

  for (const [evidenceIndex, evidence] of finding.evidence.entries()) {
    for (const source of [evidence.json_path, evidence.source]) {
      if (!source) continue;
      const reference = referenceFromSource(source, dataIndex, evidenceIndex);
      if (reference) references.push(reference);
    }
  }

  references.push(
    ...referencesFromTexts(
      [finding.finding, finding.recommendation],
      dataIndex,
    ),
  );

  return dedupeReferences(references);
}

function referencesFromFinding(
  finding: RecordLike,
  fullResult: RecordLike,
  dataIndex: DataIndex,
): ProjectDataOverviewIssueReference[] {
  const references: ProjectDataOverviewIssueReference[] = [];
  const dayNumber = numberValue(finding.day_number);
  const itemType = normalizeItemType(stringValue(finding.item_type));
  if (dayNumber != null) {
    references.push(
      normalizeReference(
        {
          kind: itemType ? "itineraryItem" : "itineraryDay",
          dayNumber,
          itemType,
          source: `day_number:${dayNumber}`,
        },
        dataIndex,
      ),
    );
  }

  references.push(
    ...referencesFromTexts(
      [
        finding.source_field,
        finding.source,
        finding.evidence,
        finding.check_point,
        finding.finding,
      ],
      dataIndex,
    ),
  );

  const evidenceRefs = asNumberArray(finding.evidence_refs);
  if (evidenceRefs.length > 0) {
    const entries = collectEvidenceEntries(fullResult);
    for (const evidenceRef of evidenceRefs) {
      const entry = entries[evidenceRef];
      if (entry) {
        const reference = referenceFromSource(
          entry.evidence.source,
          dataIndex,
          entry.globalIndex,
        );
        if (reference) references.push(reference);
      }
    }
  }

  references.push(...referencesFromEvidence(finding, dataIndex));
  return dedupeReferences(references);
}

function referencesFromEvidence(
  value: unknown,
  dataIndex: DataIndex,
): ProjectDataOverviewIssueReference[] {
  return dedupeReferences(
    collectEvidenceEntries(value)
      .map((entry) =>
        referenceFromSource(
          entry.evidence.source,
          dataIndex,
          entry.globalIndex,
        ),
      )
      .filter(
        (reference): reference is ProjectDataOverviewIssueReference =>
          reference != null,
      ),
  );
}

function referencesFromTexts(
  values: unknown[],
  dataIndex: DataIndex,
): ProjectDataOverviewIssueReference[] {
  const references: ProjectDataOverviewIssueReference[] = [];
  for (const value of values) {
    const text = stringValue(value);
    if (!text) continue;
    const direct = referenceFromSource(text, dataIndex);
    if (direct) references.push(direct);

    for (const dayNumber of extractDayNumbers(text)) {
      references.push(
        normalizeReference(
          {
            kind: "itineraryDay",
            dayNumber,
            source: text,
          },
          dataIndex,
        ),
      );
    }
  }
  return dedupeReferences(references);
}

function referenceFromSource(
  source: string,
  dataIndex: DataIndex,
  evidenceIndex?: number,
): ProjectDataOverviewIssueReference | null {
  const target = parseProjectDataReference(source);
  if (!target) return null;
  return normalizeReference(target, dataIndex, evidenceIndex);
}

function normalizeReference(
  target: ProjectDataReferenceTarget,
  dataIndex: DataIndex,
  evidenceIndex?: number,
): ProjectDataOverviewIssueReference {
  const dayIndex =
    target.dayIndex ??
    (typeof target.dayNumber === "number"
      ? dataIndex.dayNumberToIndex.get(target.dayNumber)
      : undefined);
  const itemId = target.kind === "itineraryItem" ? target.itemId : undefined;
  const location = itemId ? dataIndex.itemIdToLocation.get(itemId) : undefined;
  const normalizedDayIndex = location?.dayIndex ?? dayIndex;
  const itemIndex =
    location?.itemIndex ??
    (target.kind === "itineraryItem" ? target.itemIndex : undefined);
  const locationKey =
    typeof normalizedDayIndex === "number" && typeof itemIndex === "number"
      ? `${normalizedDayIndex}:${itemIndex}`
      : undefined;
  const locationItemId = locationKey
    ? dataIndex.itemIdByLocation.get(locationKey)
    : undefined;
  const normalizedItemId = itemId ?? locationItemId;
  const itemType =
    target.kind === "itineraryItem" ? target.itemType : undefined;
  const exactItemKey = normalizedItemId ?? locationKey;

  if (target.kind === "itineraryItem") {
    const matchedItemKey =
      exactItemKey ??
      findUniqueItemByType(normalizedDayIndex, itemType, dataIndex);
    return {
      kind: matchedItemKey ? "item" : "day",
      source: target.source,
      dayIndex: normalizedDayIndex,
      dayNumber: target.dayNumber,
      itemIndex,
      itemId: normalizedItemId,
      itemType,
      itemKey: matchedItemKey,
      evidenceIndex,
    };
  }

  return {
    kind: "day",
    source: target.source,
    dayIndex: normalizedDayIndex,
    dayNumber: target.dayNumber,
    evidenceIndex,
  };
}

function buildDataIndex(projectData: AggregatedProjectData): DataIndex {
  const dayNumberToIndex = new Map<number, number>();
  const itemIdToLocation = new Map<
    string,
    { dayIndex: number; itemIndex: number }
  >();
  const itemIdByLocation = new Map<string, string>();
  const itemTypeByLocation = new Map<string, string | null>();

  projectData.itineraries.forEach((itinerary, dayIndex) => {
    const day = asRecord(asRecord(itinerary).itinerary_day);
    const dayNumber = numberValue(day.day_number);
    if (dayNumber != null) dayNumberToIndex.set(dayNumber, dayIndex);

    asRecords(asRecord(itinerary).itinerary_items).forEach(
      (item, itemIndex) => {
        const id = stringValue(item.id);
        if (id) {
          itemIdToLocation.set(id, { dayIndex, itemIndex });
          itemIdByLocation.set(`${dayIndex}:${itemIndex}`, id);
        }
        itemTypeByLocation.set(
          `${dayIndex}:${itemIndex}`,
          stringValue(item.itinerary_itemable_type),
        );
      },
    );
  });

  return {
    dayNumberToIndex,
    itemIdByLocation,
    itemIdToLocation,
    itemTypeByLocation,
  };
}

function findUniqueItemByType(
  dayIndex: number | undefined,
  itemType: string | undefined,
  dataIndex: DataIndex,
): string | undefined {
  if (dayIndex == null || !itemType) return undefined;
  const matches = Array.from(dataIndex.itemTypeByLocation.entries())
    .filter(
      ([key, type]) => key.startsWith(`${dayIndex}:`) && type === itemType,
    )
    .map(([key]) => key);
  return matches.length === 1 ? matches[0] : undefined;
}

function isAIIssueWorthy(result: AICheckResult): boolean {
  return (
    result.status === "warning" ||
    result.status === "fail" ||
    result.status === "error"
  );
}

function severityFromAIResult(
  result: Pick<AICheckResult, "status" | "importance">,
): ProjectDataOverviewIssueSeverity {
  if (result.status === "error" || result.status === "fail") {
    return result.importance === "low" ? "warning" : "critical";
  }
  if (result.status === "warning") return "warning";
  return "info";
}

function severityFromFinding(
  finding: RecordLike,
  result: AICheckResult,
): ProjectDataOverviewIssueSeverity {
  const explicit = stringValue(finding.severity)?.toLowerCase();
  if (explicit === "critical" || explicit === "error") return "critical";
  if (explicit === "warning" || explicit === "high") return "warning";
  if (explicit === "info" || explicit === "low") return "info";
  return severityFromAIResult(result);
}

function severityFromConsistencyResult(
  result: ConsistencyCheckResult,
): ProjectDataOverviewIssueSeverity {
  if (result.evaluationError || result.severity === "error") return "critical";
  if (result.severity === "warning") return "warning";
  return "info";
}

function statusFromFreeformFinding(
  finding: FreeformFinding,
): CheckStatus | RuleSeverity | "pass" {
  if (finding.severity === "critical") return "fail";
  if (finding.severity === "warning") return "warning";
  return "pass";
}

function importanceFromFreeformFinding(
  finding: FreeformFinding,
): CheckImportance | "medium" {
  if (finding.severity === "critical") return "high";
  if (finding.severity === "warning") return "medium";
  return "low";
}

function compareIssues(
  a: ProjectDataOverviewIssue,
  b: ProjectDataOverviewIssue,
): number {
  const severityDelta =
    severityRank(a.severity) - severityRank(b.severity) ||
    importanceRank(a.importance) - importanceRank(b.importance);
  if (severityDelta !== 0) return severityDelta;
  return a.title.localeCompare(b.title);
}

function severityRank(severity: ProjectDataOverviewIssueSeverity): number {
  if (severity === "critical") return 0;
  if (severity === "warning") return 1;
  return 2;
}

function importanceRank(importance: CheckImportance | "medium"): number {
  if (importance === "critical") return 0;
  if (importance === "high") return 1;
  if (importance === "medium") return 2;
  return 3;
}

function referenceKey(reference: ProjectDataOverviewIssueReference): string {
  return [
    reference.kind,
    reference.dayIndex ?? "",
    reference.dayNumber ?? "",
    reference.itemKey ?? "",
    reference.itemId ?? "",
    reference.itemIndex ?? "",
    reference.itemType ?? "",
  ].join(":");
}

function dedupeReferences(
  references: ProjectDataOverviewIssueReference[],
): ProjectDataOverviewIssueReference[] {
  const map = new Map<string, ProjectDataOverviewIssueReference>();
  for (const reference of references) {
    map.set(referenceKey(reference), reference);
  }
  return Array.from(map.values());
}

function preferSpecificReferences(
  references: ProjectDataOverviewIssueReference[],
): ProjectDataOverviewIssueReference[] {
  const itemDayKeys = new Set(
    references
      .filter((reference) => reference.kind === "item")
      .map(dayReferenceKey)
      .filter((key): key is string => key != null),
  );
  if (itemDayKeys.size === 0) return references;
  return references.filter((reference) => {
    if (reference.kind !== "day") return true;
    const key = dayReferenceKey(reference);
    return !key || !itemDayKeys.has(key);
  });
}

function dayReferenceKey(
  reference: ProjectDataOverviewIssueReference,
): string | null {
  if (reference.dayIndex != null) return `index:${reference.dayIndex}`;
  if (reference.dayNumber != null) return `number:${reference.dayNumber}`;
  return null;
}

function appendMapValue<K, V>(map: Map<K, V[]>, key: K, value: V) {
  const current = map.get(key);
  if (current) {
    current.push(value);
  } else {
    map.set(key, [value]);
  }
}

function extractDayNumbers(text: string): number[] {
  const numbers = new Set<number>();
  for (const match of text.matchAll(/\bDay\s*([0-9]+)/gi)) {
    const value = Number.parseInt(match[1], 10);
    if (Number.isFinite(value)) numbers.add(value);
  }
  return Array.from(numbers);
}

function normalizeItemType(value: string | null): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("Itinerary")) return value;
  const normalized = value.toLowerCase().replace(/[^a-z]/g, "");
  const map: Record<string, string> = {
    activity: "ItineraryActivity",
    car: "ItineraryHireCar",
    experience: "ItineraryExperience",
    guide: "ItineraryGuide",
    hirecar: "ItineraryHireCar",
    hotel: "ItineraryHotel",
    other: "ItineraryOther",
    restaurant: "ItineraryRestaurant",
    transport: "ItineraryTransportation",
    transportation: "ItineraryTransportation",
  };
  return map[normalized];
}

function labeledLine(label: string, value: unknown): string | null {
  const text = stringValue(value);
  return text ? `${label}: ${text}` : null;
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    const text = stringValue(value);
    if (text) return text;
  }
  return null;
}

function stringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function asNumberArray(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((item): item is number => Number.isInteger(item))
    : [];
}

function asRecord(value: unknown): RecordLike {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as RecordLike)
    : {};
}

function asRecords(value: unknown): RecordLike[] {
  return Array.isArray(value)
    ? value
        .map((item) => asRecord(item))
        .filter((item) => Object.keys(item).length > 0)
    : [];
}
